import * as ScrollArea from '@radix-ui/react-scroll-area';
import { SearchIcon } from 'lucide-react';
import { useState } from 'react';
import { Avatar } from '../../../../design-system/avatar/avatar/avatar';
import { Button } from '../../../../design-system/button/button';
import { FaIcon } from '../../../../design-system/icon/fa-icon';
import { Inline } from '../../../../design-system/inline/inline';
import { Input } from '../../../../design-system/input/input';
import { Stack } from '../../../../design-system/stack/stack';
import { CategoryView } from '../../../../types/entity-types';
import { cn } from '../../../../utils/cn';
import { getGroupedCategories } from '../../../../utils/get-grouped-categories';

type VendorConfig = {
  name: string;
  logo?: string | null;
};

type SelectVendorCategoryProps = {
  selectedCategoryId?: string;
  vendorConfig: VendorConfig;
  categories: CategoryView[];
  isLoading: boolean;
  onCreateNewCategory: () => void;
  onChangeCategory: (categoryId: string) => void;
  onCancel: () => void;
};

export const SelectVendorCategory = ({
  selectedCategoryId,
  vendorConfig,
  categories,
  isLoading,
  onCreateNewCategory,
  onChangeCategory,
  onCancel,
}: SelectVendorCategoryProps) => {
  const [search, setSearch] = useState('');
  const [selectedLevel2CategoryId, setSelectedLevel2CategoryId] = useState<string | undefined>(selectedCategoryId);

  const groupedCategories = getGroupedCategories({ categories });

  const filteredCategories = groupedCategories
    .map((category) => ({
      ...category,
      children: category.children.filter(
        (child) =>
          // Include the child if it matches the search OR if it's the selected category
          child.name.toLowerCase().includes(search.trim().toLowerCase()) || child.id === selectedLevel2CategoryId,
      ),
    }))
    .filter((category) => category.children.length > 0);

  return (
    <Stack space={4} align="stretch">
      <Inline justify="between" className="px-5">
        <h2 className="text-xl font-medium text-black">Change Category</h2>

        <Button onClick={onCreateNewCategory}>Create New</Button>
      </Inline>

      <div className="px-5">
        <Inline className="rounded-lg border border-border bg-muted px-4 py-3">
          <Avatar name={vendorConfig.name} avatarUrl={vendorConfig.logo} size="xs" className="bg-transparent" />

          <h3 className="text-sm font-medium text-black">{vendorConfig.name}</h3>
        </Inline>
      </div>

      <div className="px-5">
        <Input
          leftIcon={<SearchIcon />}
          value={search}
          placeholder="Search Category"
          onChange={(e) => setSearch(e.target.value)}
          className="bg-muted"
          autoFocus
        />
      </div>

      <Stack space={2} align="stretch">
        <ScrollArea.Root className="h-[225px] overflow-hidden">
          <ScrollArea.Viewport className="size-full">
            {filteredCategories.map((group) => (
              <Stack key={group.name} space={2} align="stretch" className="pb-2">
                <h3 className="pl-5 text-xs font-medium text-muted-foreground">{group.name}</h3>

                <div>
                  {group.children.map((subCategory) => {
                    return (
                      <Button
                        key={subCategory.id}
                        variant="ghost"
                        size="none"
                        className="group w-full rounded-none focus-visible:ring-0"
                        onClick={() => setSelectedLevel2CategoryId(subCategory.id)}
                      >
                        <Inline
                          space={2}
                          wrap="nowrap"
                          className={cn(
                            'w-full py-2 pr-2 pl-6 group-hover:cursor-pointer group-hover:bg-primary/50 group-focus-visible:bg-primary/50 odd:bg-accent',
                            {
                              'bg-primary/20 odd:bg-primary/20': selectedLevel2CategoryId === subCategory.id,
                            },
                          )}
                        >
                          <FaIcon iconName={subCategory.icon} className="text-black" />

                          <p className="text-sm font-normal text-black">{subCategory.name}</p>
                        </Inline>
                      </Button>
                    );
                  })}
                </div>
              </Stack>
            ))}
          </ScrollArea.Viewport>

          <ScrollArea.Scrollbar
            className="flex touch-none bg-muted p-0.5 transition-colors duration-[160ms] ease-out select-none hover:bg-muted data-[orientation=horizontal]:h-2.5 data-[orientation=horizontal]:flex-col data-[orientation=vertical]:w-2.5"
            orientation="vertical"
          >
            <ScrollArea.Thumb className="relative flex-1 rounded-lg bg-muted-foreground before:absolute before:top-1/2 before:left-1/2 before:size-full before:min-h-11 before:min-w-11 before:-translate-x-1/2 before:-translate-y-1/2" />
          </ScrollArea.Scrollbar>
        </ScrollArea.Root>

        <Inline space={2} className="w-full px-5">
          <Button
            variant="default"
            onClick={() => {
              if (selectedLevel2CategoryId) {
                onChangeCategory(selectedLevel2CategoryId);
              }
            }}
            isLoading={isLoading}
            disabled={!selectedLevel2CategoryId || selectedLevel2CategoryId === selectedCategoryId}
            className="flex-1"
          >
            Save
          </Button>

          <Button variant="secondary" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
        </Inline>
      </Stack>
    </Stack>
  );
};
